import type { Device, Devices } from 'src/types/Device';
import type { DashboardRecord } from './dash-types';
import { isMockLightDevice } from 'src/helpers/dev/mock_light_ids';

/** Virtual, client-only dashboard id. Negative ids cannot collide with persisted dashboards. */
export const ALL_DEVICE_WIDGETS_DASHBOARD_ID = -105;
export const ALL_DEVICE_WIDGETS_DASHBOARD_ROUTE = '/-105';

export const ALL_DEVICE_WIDGETS_DASHBOARD_TAB = {
	id: ALL_DEVICE_WIDGETS_DASHBOARD_ID,
	key: 'all_device_widgets',
	text: 'All Device Widgets',
	defaultText: 'All Device Widgets',
	route: ALL_DEVICE_WIDGETS_DASHBOARD_ROUTE,
	icon: 'fas fa-toggle-on',
	backgroundColor: 'color-0',
} as const;

const sortHomeDevices = (deviceList: Device[]): Device[] =>
	[...deviceList].sort((a, b) => {
		const byPosition = (a.position ?? 0) - (b.position ?? 0);
		if (byPosition !== 0) return byPosition;
		return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
	});

const toDeviceList = (devices: Devices | Device[]): Device[] => {
	const list = Array.isArray(devices) ? devices : Object.values(devices);
	return sortHomeDevices(list.filter((device) => Boolean(device?.id) && !isMockLightDevice(device)));
};

export const buildAllDeviceWidgetsDashboard = (devices: Devices | Device[] = {}): DashboardRecord => {
	const cards = toDeviceList(devices).map((device, position) => ({
		id: device.id,
		_type: 'devices' as const,
		position,
	}));

	return {
		id: ALL_DEVICE_WIDGETS_DASHBOARD_ID,
		name: 'All Device Widgets',
		text: 'All Device Widgets',
		position: 2,
		route: ALL_DEVICE_WIDGETS_DASHBOARD_ROUTE,
		protected: true,
		schemaVersion: 1,
		icon: ALL_DEVICE_WIDGETS_DASHBOARD_TAB.icon,
		backgroundColor: ALL_DEVICE_WIDGETS_DASHBOARD_TAB.backgroundColor,
		// Left: whole-card Control. Right: same type with in-slot button (Advanced placement).
		dashWidgets: [
			{
				id: 'all-device-quick-control',
				type: 'dashQuickDev',
				title: 'Control card (tap anywhere)',
				col: 'left',
				position: 0,
				showWidgetTitle: true,
				cards,
			},
			{
				id: 'all-device-widget',
				type: 'dashQuickDev',
				title: 'Control card (button in slot)',
				col: 'right',
				position: 0,
				showWidgetTitle: true,
				deviceCardConfig: { controlPlacement: 'button' },
				cards,
			},
		],
	};
};

export const isAllDeviceWidgetsDashboardId = (id: string | number | undefined | null): boolean =>
	Number(id) === ALL_DEVICE_WIDGETS_DASHBOARD_ID;
